import React, { useState } from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';

import { rem } from '../../utilities/style';
import { colors } from '../../theme/colors';
import { fontWeights } from '../../theme/type';
import { headers } from '../../styles/type';

import Button from '../buttons/Button';
import Select from '../Select';
import TextRenderer from '../TextRenderer';

const MobileDropdownContainer = styled.div`
  margin: 0 auto ${rem(40)};
  max-width: 450px;
`;

const MobileDropdownLabel = styled.div`
  color: ${colors.blue['800']};
  font-weight: ${fontWeights.semiBold};
  margin-bottom: 8px;
`;

const MobileDropdown = styled(Select)`
  display: block;
`;

const FeaturesTable = styled.div`
  position: relative;
  z-index: 0;

  ${Button} {
    margin-top: ${rem(24)};
  }
`;

const Tabs = styled.div`
  position: sticky;
  z-index: 1;
  top: ${rem(50)};
  display: flex;
  width: calc(100% + ${rem(40)});
  margin-left: ${rem(-20)};
  padding-left: ${rem(20)};
  overflow-x: auto;
  background: ${colors.white};
`;

const TabLabel = styled.button`
  ${headers.eyebrow}
  position: relative;
  display: flex;
  align-items: flex-end;
  padding: ${rem(12)};
  border: 0;
  background: 0;
  white-space: nowrap;

  &:focus,
  &:hover {
    color: ${colors.green['500']};
  }

  &::before {
    content: '';
    position: absolute;
    width: 100%;
    height: 5px;
    left: 0;
    bottom: 0;
    background-color: ${colors.blue['50']};
    transform: scaleX(0);
    transition: transform 400ms ease;
  }

  ${({ active }) => active && `
    &::before {
      transform: none;
      background-color: ${colors.green['500']};
    }
  `}
`;

const TabContent = styled.div`
  padding-top: ${rem(16)};
`;

const Features = styled.ul``;

// Should conditionally render instead of hiding with styles
const Feature = styled.li`
  padding: ${rem(8)} 0;
  border-bottom: 1px solid ${colors.gray['200']};
  color: ${colors.gray['600']};

  ${({ available }) => !available && `
    display: none;
  `};
`;

const FeaturesTableStacked = ({
  className,
  tableData,
  showMobileDropdown = false,
  mobileDropdownLabel
}) => {
  const [activeIndex, setActiveIndex] = useState(0);

  return (
    <FeaturesTable className={className}>
      {showMobileDropdown ? (
        <MobileDropdownContainer>
          {mobileDropdownLabel && (
            <MobileDropdownLabel>
              <TextRenderer text={mobileDropdownLabel} />
            </MobileDropdownLabel>
          )}
          <MobileDropdown
            options={tableData.columns.map((column, index) => ({
              value: index,
              label: column
            }))}
            defaultValue={activeIndex}
            onChange={(index) => setActiveIndex(index)}
            fullWidth
          />
        </MobileDropdownContainer>
      ) : (
        <Tabs>
          {tableData.columns.map((column, index) => (
            <TabLabel
              // eslint-disable-next-line react/no-array-index-key
              key={index}
              active={index === activeIndex}
              onClick={() => setActiveIndex(index)}
            >
              <TextRenderer text={column} />
            </TabLabel>
          ))}
        </Tabs>
      )}
      <TabContent>
        {tableData.values.map((rowData, i) => {
          const rowLabel = tableData.rows[i];
          const columnData = rowData[activeIndex];
          const isDataString = typeof columnData === 'string';

          return (
            // eslint-disable-next-line react/no-array-index-key
            <Features key={i}>
              <Feature available={columnData}>
                {isDataString ? (
                  <>
                    <TextRenderer text={rowLabel} />
                    {': '}
                    <TextRenderer text={columnData} />
                  </>
                ) : (
                  <TextRenderer text={rowLabel} />
                )}
              </Feature>
            </Features>
          );
        })}
        {tableData.links && tableData.links[activeIndex] !== undefined && (
          <Button to={tableData.links[activeIndex].url}>
            <TextRenderer text={tableData.links[activeIndex].title} />
          </Button>
        )}
      </TabContent>
    </FeaturesTable>
  );
};

FeaturesTableStacked.propTypes = {
  className: PropTypes.string,
  tableData: PropTypes.shape({
    columns: PropTypes.arrayOf(PropTypes.string),
    rows: PropTypes.arrayOf(PropTypes.string),
    values: PropTypes.arrayOf(
      PropTypes.arrayOf(
        PropTypes.oneOfType([PropTypes.string, PropTypes.bool, PropTypes.number])
      )
    ),
    links: PropTypes.arrayOf(PropTypes.shape({
      title: PropTypes.string,
      url: PropTypes.string
    }))
  }),
  showMobileDropdown: PropTypes.bool,
  mobileDropdownLabel: PropTypes.string
};

export default FeaturesTableStacked;
